import { postGenerateCode } from '@yandex-int/k-common/client/api';
import { showNetworkError as showNetworkErrorAction, SimpleCallback } from 'platform-components';
import { copyTextToClipboard } from 'platform-components/utils';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { generatePath } from 'react-router';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { getFullUrl } from 'utils/get-full-url';

import { formatCode } from './utils';

interface ContainerProps {
    teamGrade?: number;
    teamId: number;
    showNetworkError: typeof showNetworkErrorAction;
    onRequestClose: SimpleCallback;
    open: boolean;
}

interface ContextType {
    staticUrls: Record<string, string>;
}

const CLOSE_POPUP_TIMEOUT = 3000;

const statefulContainer = (PresentationComponent: React.ComponentType<any>) => {
    const StatefulContainerComponents = (
        { showNetworkError, open, teamId, teamGrade, onRequestClose, ...props }: ContainerProps,
        { staticUrls }: ContextType
    ) => {
        const [isLoading, setLoading] = useState(false);
        const [code, setCode] = useState('');

        const [generatePopupShown, setGeneratePopupShown] = useState(false);
        const [copyPopupShown, setCopyPopupShown] = useState(false);
        const generatePopupTimeout = useRef<ReturnType<typeof setTimeout>>();
        const copyPopupTimeout = useRef<ReturnType<typeof setTimeout>>();

        const [generateLinkPopupShown, setGenerateLinkPopupShown] = useState(false);
        const [copyLinkPopupShown, setCopyLinkPopupShown] = useState(false);
        const generateLinkPopupTimeout = useRef<ReturnType<typeof setTimeout>>();
        const copyLinkPopupTimeout = useRef<ReturnType<typeof setTimeout>>();

        const generateCode = useCallback(async () => {
            setLoading(true);
            setGeneratePopupShown(false);

            const { code: newCode } = await postGenerateCode(teamId);

            setLoading(false);
            setCode(newCode);

            if (code) {
                setGeneratePopupShown(true);
            }

            if (generatePopupTimeout.current) {
                clearTimeout(generatePopupTimeout.current);
            }

            generatePopupTimeout.current = setTimeout(() => {
                setGeneratePopupShown(false);
            }, CLOSE_POPUP_TIMEOUT);
        }, [code, teamId]);

        useEffect(() => {
            if (open) {
                generateCode();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);

        const generateLink = useCallback(async () => {
            setLoading(true);
            setGenerateLinkPopupShown(false);

            const { code: newCode } = await postGenerateCode(teamId);

            setLoading(false);
            setCode(newCode);

            if (code) {
                setGenerateLinkPopupShown(true);
            }

            if (generateLinkPopupTimeout.current) {
                clearTimeout(generateLinkPopupTimeout.current);
            }

            generateLinkPopupTimeout.current = setTimeout(() => {
                setGenerateLinkPopupShown(false);
            }, CLOSE_POPUP_TIMEOUT);
        }, [code, teamId]);

        const copyCode = useCallback(() => {
            try {
                ym('reachGoal', 'teacher_my_students_class_copy_class_code', {
                    team_id: teamId,
                    code,
                });

                copyTextToClipboard(formatCode(code));
                setCopyPopupShown(true);
                copyPopupTimeout.current = setTimeout(() => {
                    setCopyPopupShown(false);
                }, CLOSE_POPUP_TIMEOUT);
            } catch (err) {
                showNetworkError();
            }
        }, [teamId, code, showNetworkError]);

        const link = code ? getFullUrl(generatePath(staticUrls.applyCode, { code })) : '';

        const copyLink = useCallback(() => {
            try {
                ym('reachGoal', 'teacher_my_students_class_copy_invite_link', {
                    team_id: teamId,
                    code,
                });

                copyTextToClipboard(link);
                setCopyLinkPopupShown(true);
                copyLinkPopupTimeout.current = setTimeout(() => {
                    setCopyLinkPopupShown(false);
                }, CLOSE_POPUP_TIMEOUT);
            } catch (err) {
                showNetworkError();
            }
        }, [code, teamId, link, showNetworkError]);

        const closeModal = useCallback(() => {
            setCode('');
            onRequestClose();
        }, [onRequestClose]);

        return (
            <PresentationComponent
                code={formatCode(code)}
                copyCode={copyCode}
                copyLink={copyLink}
                generateCode={generateCode}
                generateLink={generateLink}
                isLoading={isLoading}
                link={link}
                onRequestClose={closeModal}
                open={open}
                shouldShowCopyCodePopup={copyPopupShown}
                shouldShowCopyLinkPopup={copyLinkPopupShown}
                shouldShowGenerateCodePopup={generatePopupShown}
                shouldShowGenerateLinkPopup={generateLinkPopupShown}
                teamGrade={teamGrade}
                teamId={teamId}
                {...props}
            />
        );
    };

    StatefulContainerComponents.contextTypes = {
        staticUrls: PropTypes.object,
    };

    const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
        return bindActionCreators({ showNetworkError: showNetworkErrorAction }, dispatch);
    };

    return connect(null, mapDispatchToProps)(StatefulContainerComponents);
};
export default statefulContainer;
